import { Box } from '@mui/material'
import type { DateTime } from 'luxon'
import { useMemo } from 'react'

import type { FormattedRow } from '@/features/bessDashboard/types'
import { useBidLadderForecast } from '@/features/bidding/hooks/useBidLadderForecast'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { ForecastMarketPair, PricingStrategyConfig } from '@/features/bidding/types/forecastPricing'
import { convertBidLevelInputsToBidLevels } from '@/features/bidding/utils/bidLadderInput/convertToBidLevels'
import { calculateOpportunityCosts } from '@/features/bidding/utils/cascading/bidLadder/calculateOpportunityCosts'
import { getMarketProgramNames } from '@/features/bidding/utils/forecast/marketProgramNames'
import { getMarketVolumeGranularity } from '@/features/bidding/utils/marketCapabilities'

import BidLadderOverviewChart from './BidLadderOverviewChart'

type BidLadderChartsProps = {
  bidLevelInputs: BidLevelInput[]
  forecastsData: FormattedRow[] | undefined
  selectedDeliveryDay: DateTime
  totalMWAvailable: number
  /** Market to display forecast data for */
  market: ForecastMarketPair
  height?: number
  /** Pricing strategy config for forecast-based pricing */
  pricingConfig?: PricingStrategyConfig
  aggregateSuggestedBids?: AggregateSuggestedBidResponse | null
  aggregateAvailability?: AggregateAvailabilityResponse | null
}

function BidLadderCharts({
  bidLevelInputs,
  forecastsData,
  selectedDeliveryDay,
  totalMWAvailable,
  market,
  height = 200,
  pricingConfig,
  aggregateSuggestedBids,
  aggregateAvailability,
}: Readonly<BidLadderChartsProps>) {
  // Convert BidLevelInput[] to BidLevel[] for chart visualization
  // Pass pricingConfig to derive forecast prices when in forecast mode
  const granularityMW = getMarketVolumeGranularity(market).stepMW
  const bidLevels = useMemo(
    () =>
      convertBidLevelInputsToBidLevels(
        bidLevelInputs,
        pricingConfig,
        forecastsData,
        selectedDeliveryDay,
        aggregateSuggestedBids,
        aggregateAvailability,
        granularityMW,
      ),
    [
      bidLevelInputs,
      pricingConfig,
      forecastsData,
      selectedDeliveryDay,
      aggregateSuggestedBids,
      aggregateAvailability,
      granularityMW,
    ],
  )

  // Get market program names for the selected market
  const { up: upProgramName, down: downProgramName } = getMarketProgramNames(market)

  // Fetch forecast data for both up and down regulation markets
  // primaryForecastData is for up-regulation (displayed on right side in green)
  // referenceForecastData is for down-regulation (displayed on left side in red)
  const primaryForecastData = useBidLadderForecast(forecastsData, upProgramName, selectedDeliveryDay)
  const referenceForecastData = useBidLadderForecast(forecastsData, downProgramName, selectedDeliveryDay)

  // Get opportunity cost for other markets
  const opCostData = calculateOpportunityCosts(forecastsData, selectedDeliveryDay)

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <BidLadderOverviewChart
        avgClearingPrice={primaryForecastData?.avgClearingPrice ?? 0}
        bidLevels={bidLevels}
        clearingRangeHigh={primaryForecastData?.clearingRangeHigh ?? 0}
        clearingRangeLow={primaryForecastData?.clearingRangeLow ?? 0}
        expectedDemandMW={270}
        height={height}
        opportunityCost={opCostData?.daily}
        primaryForecastData={primaryForecastData ?? undefined}
        referenceForecastData={referenceForecastData ?? undefined}
        totalMWAvailable={totalMWAvailable}
      />
    </Box>
  )
}

export default BidLadderCharts
